import {
  ImageModelDef, ImageStylePreset, AspectRatio,
  QualityDimensionMap,
} from '../../core/types/image-generation.types';

// === Model Definitions ===
export const IMAGE_MODELS: ImageModelDef[] = [
  { id: 'soul-2.0', name: 'Nano Banana Pro', tier: 'premium', description: 'Highest quality photorealistic generation with exceptional detail', maxResolution: 4096, supportsReferenceImages: true },
  { id: 'nano-banana-pro', name: 'Soul 2.0', tier: 'standard', description: 'Fast generation with good quality-speed balance', maxResolution: 2048, supportsReferenceImages: true },
  { id: 'nano-banana-2', name: 'Nano Banana 2', tier: 'standard', description: 'Fast image generation powered by Gemini 3.1 Flash', maxResolution: 2048, supportsReferenceImages: false },
  { id: 'seedream-5.0', name: 'Seedream 5.0', tier: 'premium', description: 'Creative artistic generation with strong style adherence', maxResolution: 4096, supportsReferenceImages: true },
  { id: 'flux-kontext', name: 'Flux Kontext', tier: 'standard', description: 'Context-aware generation with scene understanding', maxResolution: 2048, supportsReferenceImages: false },
  { id: 'gpt-image-1.5', name: 'GPT Image 1.5', tier: 'premium', description: 'Text-to-image with strong prompt understanding', maxResolution: 4096, supportsReferenceImages: false },
];

// === Quality Tiers ===
export const IMAGE_QUALITY_TIERS: QualityDimensionMap[] = [
  { quality: '512', baseDimension: 512 },
  { quality: '1K', baseDimension: 1024 },
  { quality: '2K', baseDimension: 2048 },
  { quality: '4K', baseDimension: 4096 },
];

// === Aspect Ratios ===
export const ASPECT_RATIOS: { value: AspectRatio; label: string; ratio: [number, number]; tag?: string }[] = [
  { value: '1:1', label: '1:1', ratio: [1, 1] },
  { value: '3:4', label: '3:4', ratio: [3, 4] },
  { value: '2:3', label: '2:3', ratio: [2, 3] },
  { value: '9:16', label: '9:16', ratio: [9, 16] },
  { value: '3:2', label: '3:2', ratio: [3, 2] },
  { value: '4:3', label: '4:3', ratio: [4, 3] },
  { value: '16:9', label: '16:9', ratio: [16, 9], tag: 'Cinematic' },
  { value: '21:9', label: '21:9', ratio: [21, 9], tag: 'Cinematic' },
  { value: 'free', label: 'Free', ratio: [0, 0] },
];

// === UI Options ===
export const IMAGE_STYLE_PRESETS: { value: ImageStylePreset; label: string }[] = [
  { value: 'realistic', label: 'Realistic' },
  { value: 'cinematic', label: 'Cinematic' },
  { value: 'anime', label: 'Anime' },
  { value: 'illustration', label: 'Illustration' },
  { value: 'pixel-art', label: 'Pixel Art' },
  { value: 'coc', label: 'COC' },
  { value: 'immortal-legend', label: 'Immortal Legend' },
  { value: 'custom', label: 'Custom' },
];

// === Limits ===
export const MAX_DIMENSION = 4096;

// === Utility Functions ===

/**
 * Compute width and height from quality preset and aspect ratio.
 * The base dimension is applied to the larger side, and the smaller side
 * is computed from the ratio. Results are rounded to nearest multiple of 8.
 */
export function computeDimensions(
  quality: string,
  aspectRatio: AspectRatio,
): { width: number; height: number } {
  const tier = IMAGE_QUALITY_TIERS.find(t => t.quality === quality);
  if (!tier) return { width: 1024, height: 1024 };

  const base = tier.baseDimension;

  if (aspectRatio === 'free' || aspectRatio === '1:1') {
    return { width: Math.min(base, MAX_DIMENSION), height: Math.min(base, MAX_DIMENSION) };
  }

  const ratioEntry = ASPECT_RATIOS.find(r => r.value === aspectRatio);
  if (!ratioEntry) return { width: base, height: base };

  const [rw, rh] = ratioEntry.ratio;

  let width: number;
  let height: number;

  if (rw >= rh) {
    width = base;
    height = Math.round((base * rh) / rw);
  } else {
    height = base;
    width = Math.round((base * rw) / rh);
  }

  // Round to nearest 8
  width = Math.round(width / 8) * 8;
  height = Math.round(height / 8) * 8;

  // Cap at MAX_DIMENSION
  if (width > MAX_DIMENSION) {
    const scale = MAX_DIMENSION / width;
    width = MAX_DIMENSION;
    height = Math.round((height * scale) / 8) * 8;
  }
  if (height > MAX_DIMENSION) {
    const scale = MAX_DIMENSION / height;
    height = MAX_DIMENSION;
    width = Math.round((width * scale) / 8) * 8;
  }

  return { width, height };
}

/**
 * Detect the closest standard aspect ratio for given dimensions.
 * Uses +/-2% tolerance. Returns 'free' if no match.
 */
export function detectAspectRatio(w: number, h: number): AspectRatio {
  const ratio = w / h;
  const TOLERANCE = 0.02;

  const checks: { value: AspectRatio; expected: number }[] = [
    { value: '1:1', expected: 1 },
    { value: '4:3', expected: 4 / 3 },
    { value: '3:4', expected: 3 / 4 },
    { value: '16:9', expected: 16 / 9 },
    { value: '9:16', expected: 9 / 16 },
    { value: '2:3', expected: 2 / 3 },
    { value: '3:2', expected: 3 / 2 },
    { value: '21:9', expected: 21 / 9 },
  ];

  for (const check of checks) {
    if (Math.abs(ratio - check.expected) / check.expected <= TOLERANCE) {
      return check.value;
    }
  }

  return 'free';
}
